import { Box, Button, Link, SpaceBetween, Table, TableProps } from '@amzn/awsui-components-core';
import arraySort from 'array-sort';
import { ContentHistory, WorkflowState } from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';
import React, { useContext, useState } from 'react';
import { extractUserAlias, getUserRoles } from '../../api/UtilsApi';
import constants from '../../common/constants';
import { AppContext } from '../../contexts/AppContext';
import { TableHeader } from '../common/CommonComponents';
import ContentHistoryDiffViewer from './ContentHistoryDiffViewer';
import { ContentViewerContext } from './ContentViewerContext';
import WorkflowHistoryEvents from './WorkflowHistoryEvents';
interface PublicationHistoryProps {
  contentId: string;
  workflowId: number; // Current publication workflow Id
  visible?: boolean;
  workflows: ContentHistory[];
}

const historyStateToNameMap = new Map<WorkflowState, string>([
  [WorkflowState.UNPUBLISHED, 'Unpublished'],
  [WorkflowState.IN_REVIEW, 'Submitted for review'],
  [WorkflowState.REVIEWED, 'Peer approved'],
  [WorkflowState.REJECTED, 'Peer rejected'],
  [WorkflowState.PUBLISHED, 'Internally published'],
  [WorkflowState.FINALIZING, 'Internally published'],
  [WorkflowState.REVOKED, 'Partner published'],
  [WorkflowState.FINALIZED, 'Partner published'],
  [WorkflowState.DISTRIBUTED, 'Public APG'],
  [WorkflowState.DELETED, 'Deleted'],
]);

const PublicationHistory = (props: PublicationHistoryProps) => {
  const { visible, workflows } = props;
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDiff, setShowDiff] = useState(false);

  const viewerCtx = useContext(ContentViewerContext);
  const appContext = useContext(AppContext);

  const { coAuthors, peerReviewers } = viewerCtx;

  if (!visible) {
    return null;
  }

  const changeTableSelection = (
    e: CustomEvent<TableProps.SelectionChangeDetail<ContentHistory>>,
  ) => {
    // @ts-expect-error TS2345 - strict migration
    setSelectedItems(e.detail.selectedItems);
    setShowDiff(false);
  };

  const compareSelectedItems = () => {
    setShowDiff(true);
  };

  const renderHistoryDiffView = () => {
    const olderVersion =
      // @ts-expect-error TS2339 - strict migration
      selectedItems[0].workflowId > selectedItems[1].workflowId
        ? selectedItems[1]
        : selectedItems[0];
    const newerVersion =
      // @ts-expect-error TS2339 - strict migration
      selectedItems[0].workflowId > selectedItems[1].workflowId
        ? selectedItems[0]
        : selectedItems[1];
    return (
      <div>
        <SpaceBetween direction="vertical" size="l">
          {/* @ts-expect-error TS2322 - strict migration */}
          <ContentHistoryDiffViewer oldHistory={olderVersion} newHistory={newerVersion} />
        </SpaceBetween>
      </div>
    );
  };

  const emptyTableTemplate = (
    <Box textAlign="center">
      <b>{constants.PUBLICATION_HISTORY_EMPTY_MESSAGE}</b>
    </Box>
  );

  const columnDefinitions = [
    {
      cell: (item: ContentHistory) => `${item.workflowId}.0`,
      header: 'Version',
      id: 'version',
    },
    {
      cell: (item: ContentHistory) => {
        const ownerAlias = ` (${extractUserAlias(item.ownerId)})`;
        return item.ownerProfile ? `${item.ownerProfile.name}${ownerAlias}` : item.ownerId;
      },
      header: 'Author',
      id: 'author',
    },
    {
      cell: (item: ContentHistory) => item.publicationDate.toLocaleString(DateTime.DATE_FULL),
      header: 'Date',
      id: 'publicationDate',
    },
    {
      cell: (item: ContentHistory) => historyStateToNameMap.get(item.state),
      header: 'Status',
      id: 'status',
    },
    {
      cell: (item: ContentHistory) => {
        const href = `/content-history-viewer/${item.contentId}/${item.workflowId}`;
        return item.state === WorkflowState.DELETED ? (
          'N/A'
        ) : (
          <Link variant="secondary" href={href}>
            View
          </Link>
        );
      },
      header: '',
      id: 'historyView',
    },
  ];

  // @ts-expect-error TS18048 - strict migration
  const userRole = getUserRoles(appContext.authnInfo.userProfile, {
    coAuthorIds: coAuthors?.map((ca) => ca.userId),
    peerReviewerIds: peerReviewers?.map((pr) => pr.userId),
  });

  const eventDetailsContainer =
    selectedItems.length === 1 ? (
      <SpaceBetween direction="vertical" size="xxl">
        <hr />
        {/* @ts-expect-error TS2339 - strict migration */}
        <Box variant="h2">{`Version ${selectedItems[0].workflowId}.0 history`}</Box>
        <WorkflowHistoryEvents
          // @ts-expect-error TS2339 - strict migration
          contentId={selectedItems[0].contentId}
          // @ts-expect-error TS2339 - strict migration
          workflowId={selectedItems[0].workflowId}
        />
      </SpaceBetween>
    ) : null;

  const compareButton = userRole.isSkateholder && (
    <Button disabled={selectedItems?.length !== 2} onClick={compareSelectedItems}>
      {constants.PUBLICATION_HISTORY_COMPARE_BUTTON_TEXT}
    </Button>
  );

  const compareVersionView =
    showDiff && selectedItems.length === 2 ? renderHistoryDiffView() : null;

  return workflows?.length ? (
    <SpaceBetween direction="vertical" size="xxl">
      <Table
        columnDefinitions={columnDefinitions}
        items={workflows}
        empty={emptyTableTemplate}
        selectionType={userRole.isSkateholder ? 'multi' : undefined}
        selectedItems={selectedItems}
        // @ts-expect-error TS2322 - strict migration
        onSelectionChange={changeTableSelection}
        header={
          <TableHeader
            title={constants.PUBLICATION_HISTORY}
            // @ts-expect-error TS2322 - strict migration
            description={
              userRole.isSkateholder ? constants.PUBLICATION_HISTORY_HEADER_DESCRIPTION : null
            }
            actionButtons={compareButton}
            selectedItems={selectedItems}
            totalItems={workflows}
          />
        }
        ariaLabels={{ tableLabel: 'Publication history table' }}
      />
      {eventDetailsContainer}
      {compareVersionView}
    </SpaceBetween>
  ) : null;
};

export default PublicationHistory;
